import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  TagsInput,
} from '@mantine/core';
import { IconCake, IconTrash, IconPlus, IconUser } from '@tabler/icons-react';
import {
  updateCustomerPreferences,
  upsertCustomer,
  type CustomerSummary,
  type MonthDayDto,
} from '../api-client.ts';
import { useStaffProfile } from '../profile-context.tsx';
import { tokens } from '../ui/tokens.ts';

type Kind = 'phone' | 'line' | 'email';

interface PrefDraft {
  birthdayMonth: number | '';
  birthdayDay: number | '';
  anniversaryMonth: number | '';
  anniversaryDay: number | '';
  seatingPreference: string;
  dietaryRestrictions: string[];
}

interface Draft {
  displayName: string;
  tags: string[];
  note: string;
  contactMethods: Array<{ kind: Kind; value: string }>;
  preferences: PrefDraft;
}

function fromExisting(c: CustomerSummary): Draft {
  return {
    displayName: c.displayName,
    tags: [...c.tags],
    note: c.note ?? '',
    contactMethods: c.contactMethods.map((m) => ({ kind: m.kind, value: m.value })),
    preferences: {
      // Existing customer summary doesn't include preferences yet — UI reads
      // empty defaults. When a caller passes preferences through (future
      // enhancement), this mapping picks them up without code change.
      birthdayMonth: '',
      birthdayDay: '',
      anniversaryMonth: '',
      anniversaryDay: '',
      seatingPreference: '',
      dietaryRestrictions: [],
    },
  };
}

function emptyDraft(): Draft {
  return {
    displayName: '',
    tags: [],
    note: '',
    contactMethods: [],
    preferences: {
      birthdayMonth: '',
      birthdayDay: '',
      anniversaryMonth: '',
      anniversaryDay: '',
      seatingPreference: '',
      dietaryRestrictions: [],
    },
  };
}

function toMonthDay(month: number | '', day: number | ''): MonthDayDto | null {
  if (typeof month !== 'number' || typeof day !== 'number') return null;
  return { month, day };
}

function hasAnyPreference(p: PrefDraft): boolean {
  return (
    typeof p.birthdayMonth === 'number' ||
    typeof p.anniversaryMonth === 'number' ||
    p.seatingPreference.trim().length > 0 ||
    p.dietaryRestrictions.length > 0
  );
}

export interface CustomerFormModalProps {
  opened: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  venueId: string;
  existing?: CustomerSummary | null;
}

export function CustomerFormModal({
  opened,
  onClose,
  onSaved,
  venueId,
  existing,
}: CustomerFormModalProps): JSX.Element {
  const profile = useStaffProfile();
  const preferencesEnabled = profile.capabilities.customerCrm.preferences;
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>('basic');

  useEffect(() => {
    if (!opened) return;
    setErr(null);
    setDraft(existing ? fromExisting(existing) : emptyDraft());
  }, [opened, existing]);

  const title = existing ? '編輯客戶' : '新增客戶';
  const submitDisabled = useMemo(() => {
    if (saving) return true;
    if (draft.displayName.trim().length === 0) return true;
    return false;
  }, [saving, draft.displayName]);

  const addContact = () => {
    setDraft((d) => ({
      ...d,
      contactMethods: [...d.contactMethods, { kind: 'phone', value: '' }],
    }));
  };

  const updateContact = (idx: number, patch: Partial<{ kind: Kind; value: string }>) => {
    setDraft((d) => {
      const next = d.contactMethods.slice();
      const cur = next[idx];
      if (!cur) return d;
      next[idx] = { ...cur, ...patch };
      return { ...d, contactMethods: next };
    });
  };

  const removeContact = (idx: number) => {
    setDraft((d) => ({
      ...d,
      contactMethods: d.contactMethods.filter((_, i) => i !== idx),
    }));
  };

  const onSubmit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const cleaned = draft.contactMethods
        .map((m) => ({ kind: m.kind, value: m.value.trim() }))
        .filter((m) => m.value.length > 0);
      const out = await upsertCustomer({
        ...(existing ? { customerId: existing.customerId } : {}),
        venueId,
        displayName: draft.displayName.trim(),
        contactMethods: cleaned,
        tags: draft.tags,
        note: draft.note.trim().length === 0 ? null : draft.note.trim(),
      });

      // Preferences are a separate endpoint so venues without customerCrm.preferences
      // capability still get a clean basic-customer save path. When enabled and
      // the user actually filled something, PUT preferences as a follow-up.
      if (preferencesEnabled && hasAnyPreference(draft.preferences)) {
        const birthday = toMonthDay(draft.preferences.birthdayMonth, draft.preferences.birthdayDay);
        const anniversary = toMonthDay(
          draft.preferences.anniversaryMonth,
          draft.preferences.anniversaryDay,
        );
        await updateCustomerPreferences(out.customerId, {
          birthday,
          anniversary,
          seatingPreference:
            draft.preferences.seatingPreference.trim().length === 0
              ? null
              : draft.preferences.seatingPreference.trim(),
          dietaryRestrictions: draft.preferences.dietaryRestrictions,
        });
      }

      onSaved(out.customerId);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const basicFields = (
    <Stack gap="md">
      <TextInput
        label="姓名 / 稱呼"
        placeholder="例：王小明"
        value={draft.displayName}
        onChange={(e) => setDraft({ ...draft, displayName: e.currentTarget.value })}
        required
        maxLength={100}
      />

      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={500}>
            聯絡方式
          </Text>
          <Button
            size="xs"
            variant="light"
            color={tokens.color.mantine['primary']}
            leftSection={<IconPlus size={14} />}
            onClick={addContact}
            disabled={draft.contactMethods.length >= 10}
          >
            新增
          </Button>
        </Group>
        {draft.contactMethods.length === 0 && (
          <Text size="xs" c="dimmed">
            尚未新增聯絡方式（可選）
          </Text>
        )}
        {draft.contactMethods.map((m, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-end">
            <Select
              label={i === 0 ? '類型' : undefined}
              data={[
                { value: 'phone', label: '電話' },
                { value: 'line', label: 'LINE' },
                { value: 'email', label: 'Email' },
              ]}
              value={m.kind}
              onChange={(v) => updateContact(i, { kind: (v as Kind) ?? 'phone' })}
              w={120}
            />
            <TextInput
              label={i === 0 ? '內容' : undefined}
              placeholder={
                m.kind === 'phone' ? '0912345678' : m.kind === 'line' ? '@shop' : 'user@example.com'
              }
              value={m.value}
              onChange={(e) => updateContact(i, { value: e.currentTarget.value })}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => removeContact(i)}
              aria-label="移除"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
      </Stack>

      <TagsInput
        label="標籤"
        placeholder="輸入後按 Enter"
        value={draft.tags}
        onChange={(v) => setDraft({ ...draft, tags: v })}
        maxTags={20}
      />

      <Textarea
        label="備註"
        placeholder="例：偏好靠窗，每週來一次"
        value={draft.note}
        onChange={(e) => setDraft({ ...draft, note: e.currentTarget.value })}
        autosize
        minRows={2}
        maxLength={500}
      />
    </Stack>
  );

  const preferencesFields = (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        只記錄月 / 日；不儲存完整出生年以避免不必要的 PII。
      </Text>

      <Stack gap={4}>
        <Text size="sm" fw={500}>
          生日
        </Text>
        <Group grow>
          <NumberInput
            placeholder="月"
            min={1}
            max={12}
            value={draft.preferences.birthdayMonth}
            onChange={(v) =>
              setDraft({
                ...draft,
                preferences: {
                  ...draft.preferences,
                  birthdayMonth: typeof v === 'number' ? v : '',
                },
              })
            }
          />
          <NumberInput
            placeholder="日"
            min={1}
            max={31}
            value={draft.preferences.birthdayDay}
            onChange={(v) =>
              setDraft({
                ...draft,
                preferences: {
                  ...draft.preferences,
                  birthdayDay: typeof v === 'number' ? v : '',
                },
              })
            }
          />
        </Group>
      </Stack>

      <Stack gap={4}>
        <Text size="sm" fw={500}>
          週年 / 紀念日
        </Text>
        <Group grow>
          <NumberInput
            placeholder="月"
            min={1}
            max={12}
            value={draft.preferences.anniversaryMonth}
            onChange={(v) =>
              setDraft({
                ...draft,
                preferences: {
                  ...draft.preferences,
                  anniversaryMonth: typeof v === 'number' ? v : '',
                },
              })
            }
          />
          <NumberInput
            placeholder="日"
            min={1}
            max={31}
            value={draft.preferences.anniversaryDay}
            onChange={(v) =>
              setDraft({
                ...draft,
                preferences: {
                  ...draft.preferences,
                  anniversaryDay: typeof v === 'number' ? v : '',
                },
              })
            }
          />
        </Group>
      </Stack>

      <TextInput
        label="座位 / 空間偏好"
        placeholder="例：窗邊 · 安靜 · 兒童桌"
        value={draft.preferences.seatingPreference}
        onChange={(e) =>
          setDraft({
            ...draft,
            preferences: {
              ...draft.preferences,
              seatingPreference: e.currentTarget.value,
            },
          })
        }
        maxLength={100}
      />

      <TagsInput
        label="飲食禁忌 / 過敏"
        placeholder="例：素食、無堅果（輸入後按 Enter）"
        value={draft.preferences.dietaryRestrictions}
        onChange={(v) =>
          setDraft({ ...draft, preferences: { ...draft.preferences, dietaryRestrictions: v } })
        }
        maxTags={10}
      />
    </Stack>
  );

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" centered>
      <Stack gap="md">
        {err && <Alert color="red">{err}</Alert>}

        {preferencesEnabled ? (
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconUser size={14} />}>
                基本
              </Tabs.Tab>
              <Tabs.Tab value="preferences" leftSection={<IconCake size={14} />}>
                偏好
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="basic" pt="md">
              {basicFields}
            </Tabs.Panel>
            <Tabs.Panel value="preferences" pt="md">
              {preferencesFields}
            </Tabs.Panel>
          </Tabs>
        ) : (
          basicFields
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            color={tokens.color.mantine['primary']}
            onClick={() => void onSubmit()}
            disabled={submitDisabled}
            loading={saving}
          >
            {existing ? '儲存' : '新增'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
